import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import {
	RatchetMetricType,
	RatchetingData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/data.mock.ts';
import {
	RatchetingFormDto,
	RatchetingShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/ratcheting.zod.ts';
import { DropdownOption } from '@dealstack/frontend/shared/incomeRights.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum RATCHETING_FORM_FIELD {
	metric = 'metric',
	subjects = 'subjects',
	schedule = 'schedule',
	initialEntitlement = 'initialEntitlement',
	distributeUntilBreakpoint = 'distributeUntilBreakpoint',
}

export const useRatchetingController = (ratchetingData: RatchetingData | null) => {
	const { persons } = useContext(CapManageControllerContext);

	const defaultValues = useMemo(
		() => ({
			metric: ratchetingData?.metric || RatchetMetricType.XIRR,
			subjects: ratchetingData?.subjects || [],
			// Row 0 maps to initialEntitlementPercentage; rows 1..N map to entries. Backend
			// requires entries.length >= 1, so a fresh form starts with initial + one threshold.
			schedule: ratchetingData?.schedule || [
				{ threshold: undefined, entitlement: 0 },
				{ threshold: undefined, entitlement: 0 },
			],
			initialEntitlement: ratchetingData?.initialEntitlement ?? 0,
			distributeUntilBreakpoint: ratchetingData?.distributeUntilBreakpoint ?? false,
		}),
		[ratchetingData]
	);
	const dealPersonOptions = useMemo<DropdownOption[]>(
		() =>
			persons?.map((person) => ({
				value: person.id,
				label: person.computedName,
			})) || [],
		[persons]
	);

	const ratchetingForm = useForm<RatchetingFormDto>({
		mode: 'all',
		resolver: zodResolver(RatchetingShape),
		values: defaultValues,
		shouldFocusError: false,
	});

	const scheduleFieldArray = useFieldArray({
		control: ratchetingForm.control,
		name: 'schedule',
	});

	const resetForm = () => {
		ratchetingForm.reset(defaultValues);
	};

	const addScheduleItem = () => {
		scheduleFieldArray.append({ threshold: undefined, entitlement: 0 });
	};

	const removeScheduleItem = (index: number) => {
		scheduleFieldArray.remove(index);
	};

	return {
		ratchetingForm,
		scheduleFieldArray,
		dealPersonOptions,
		resetForm,
		addScheduleItem,
		removeScheduleItem,
	};
};

export const getScheduleFieldName = (
	index: number,
	field: keyof RatchetingFormDto['schedule'][number]
) => `${RATCHETING_FORM_FIELD.schedule}.${index}.${field}` as const;

export const getRatchetingMetricOptions = (
	tCommonUI: TranslateFunction<I18Namespaces.COMMON_UI>,
	savedMetric?: RatchetMetricType
) =>
	[
		RatchetMetricType.XIRR,
		RatchetMetricType.MOIC,
		...(savedMetric === RatchetMetricType.MOIC_AND_XIRR ? [savedMetric] : []),
	].map((type) => {
		return {
			value: type,
			label: getRatchetingMetricLabel(tCommonUI, type),
		};
	});

export const getRatchetingMetricLabel = (
	tCommonUI: TranslateFunction<I18Namespaces.COMMON_UI>,
	type?: RatchetMetricType
) => {
	if (!type) {
		return;
	}

	switch (type) {
		case RatchetMetricType.XIRR:
			return tCommonUI('security_ratcheting.metric.irr');
		case RatchetMetricType.MOIC:
			return tCommonUI('security_ratcheting.metric.moic');
		case RatchetMetricType.MOIC_AND_XIRR:
			return tCommonUI('security_ratcheting.metric.moic_and_xirr');
		default:
			return type;
	}
};
